import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import {
    catchError,
    filter,
    flatMap,
    map,
    switchMap,
    withLatestFrom
} from 'rxjs/operators';
import * as fromContract from '../../../../contract-management/store';
import * as fromShared from '../../../../shared';
import { LoggingService } from '../../../../shared/services/logging/logging.service';
import { InvoiceManagementService } from '../../../management/invoice-management.service';
import * as InvoiceActions from './invoice.actions';
import { Invoice } from './invoice.model';
import * as fromInvoice from './invoice.reducer';
import * as selectors from './invoice.selectors';

@Injectable()
export class InvoiceEffects {
    getInvoices$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(InvoiceActions.getInvoices),
            switchMap(() =>
                this.service.get().pipe(
                    map(invoices => InvoiceActions.loadInvoices({ invoices })),
                    // retryBackoff({ initialInterval: 3000, maxRetries: 3 }),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        );
    });
    postInvoices$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(InvoiceActions.postInvoices),
            // withLatestFrom(
            //     this.store.pipe(select(selectors.selectAll))
            // ),
            // filter(([, items]) => items.length === 0),
            // switchMap(([action, ]) =>
            switchMap(action =>
                this.service
                    .post(
                        action.startDate,
                        action.endDate,
                        action.contractList,
                        action.showInactive,
                        action.showInvoicesOnly
                    )
                    .pipe(
                        map(invoices =>
                            InvoiceActions.loadInvoices({ invoices })
                        ),
                        // retryBackoff({ initialInterval: 3000, maxRetries: 3 }),
                        catchError(fromShared.Errors.handleServiceError)
                    )
            )
        );
    });
    postUpdateInvoices$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(InvoiceActions.postUpdateInvoices),
            switchMap(action =>
                this.service
                    .post(
                        action.startDate,
                        action.endDate,
                        action.contractList,
                        action.showInactive,
                        action.showInvoicesOnly,
                        action.resetInvoiceSchedule
                    )
                    .pipe(
                        map(invoices =>
                            InvoiceActions.upsertInvoices({ invoices })
                        ),
                        // retryBackoff({ initialInterval: 3000, maxRetries: 3 }),
                        catchError(fromShared.Errors.handleServiceError)
                    )
            )
        );
    });
    putInvoices$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(InvoiceActions.putInvoices),
            switchMap(action =>
                this.service.put(action.invoices).pipe(
                    withLatestFrom(
                        this.store.pipe(
                            //TODO: move to the factory selector for selectInvoicesByContractId
                            // select(selectors.selectInvoicesByContractId(action.invoices.map(x => x.ContractListID))),
                            select(selectors.selectInvoicesByContractId, {
                                ids: action.invoices.map(x => x.ContractListID)
                            }),
                            // map(invoices => invoices.reduce((a, c) => c)),
                            map(
                                invoices =>
                                    fromShared.uniqBy(
                                        invoices,
                                        x => x.ContractListID
                                    ) as Invoice[]
                            ),
                            map(invoices =>
                                invoices.filter(i =>
                                    action.invoices.find(
                                        x =>
                                            x.ContractListID ===
                                                i.ContractListID &&
                                            (x.ScheduledStartDate.getTime() !==
                                                i.ScheduledStartDate.getTime() ||
                                                x.ScheduledEndDate.getTime() !==
                                                    i.ScheduledEndDate.getTime())
                                    )
                                )
                            )
                        )
                    ),
                    flatMap(([updating, existing]) => [
                        InvoiceActions.upsertInvoices({
                            invoices: updating.Items
                        }),
                        existing && existing.length > 0
                            ? InvoiceActions.postUpdateInvoices({
                                  startDate: action.startDate,
                                  endDate: action.endDate,
                                  contractList: existing.map(
                                      y => y.ContractListID
                                  )
                              })
                            : { type: 'EMPTY' }
                    ]),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        );
    });
    putInvoiceCycle$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(InvoiceActions.putInvoiceCycle),
            switchMap(action =>
                this.service.putInvoiceCycle(action.item).pipe(
                    map(data => ({
                        ...data,
                        Items: data.Items.filter(x => !!x)
                    })),
                    filter(data => data && data.Items.length > 0),
                    map(data =>
                        fromContract.upsertContractManagement(
                            data.Items[0] as any
                        )
                    ),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        );
    });
    getActiveContracts$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(InvoiceActions.getActiveContracts),
            switchMap(action =>
                this.service
                    .getActiveContract(
                        action.startDate,
                        action.endDate,
                        action.showInactive
                    )
                    .pipe(
                        map(contracts =>
                            InvoiceActions.loadActiveContracts({
                                contracts: contracts.map(x => ({
                                    label: x.ContractDesc,
                                    value: x.ContractListID
                                }))
                            })
                        ),
                        // retryBackoff({ initialInterval: 3000, maxRetries: 3 }),
                        catchError(fromShared.Errors.handleServiceError)
                    )
            )
        );
    });

    constructor(
        private actions$: Actions,
        private service: InvoiceManagementService,
        private store: Store<fromInvoice.State>,
        private log: LoggingService
    ) {}
}
